import React, { useState, useCallback, useEffect, useRef } from "react";
import VideoPreview from "./VideoPreview.jsx";
import VideoTimeline from "./VideoTimeline.jsx";
import MontageToolbar from "./MontageToolbar.jsx";
import TransitionEditor from "./TransitionEditor.jsx";
import SegmentCard from "./SegmentCard.jsx";
import MediaSelector from "./MediaSelector.jsx";
import { t } from "../i18n";
import "./MontageEditor.css";

/**
 * MontageEditor - Timeline Orchestrator layout
 *
 * 3-column layout:
 *  - Center: Preview + Toolbar + Timeline (persistent)
 *  - Right rail: Segment Inspector (contextual)
 *
 * Replaces the old wizard-based step navigation.
 */
export default function MontageEditor({
  montage,
  segments = [],
  // Playback
  currentTimeMs = 0,
  isPlaying = false,
  activeSegmentId = null,
  onPlayPause,
  onSeek,
  onTimeUpdate,
  previewUrl = "",
  previewStatus = "",
  previewLoading = false,
  // Segment actions
  onSegmentClick,
  onSegmentReorder,
  onUpdateSegment,
  onSaveSegment,
  onDeleteSegment,
  onAddSegment,
  onRegenerateSegment,
  onEnhanceSegmentPrompt,
  // Transition
  onSaveTransition,
  // Global actions
  onSave,
  onEnhanceAll,
  onGenerateImages,
  onGenerateVideos,
  // Quality
  imageQuality,
  videoQuality,
  onSetImageQuality,
  onSetVideoQuality,
  // Loading states
  saving = false,
  loading = false,
  enhancingPrompts = false,
  generatingImages = false,
  generatingVideos = false,
  exporting = false,
  onExport,
  harmonizing = false,
  onHarmonize,
  // Reference media (montage-wide)
  referenceMedia = [],
  onUpdateReferences,
  onAddReferenceFromSegment,
  referencePickerTarget = "montage_ref",
  referencePickerReturn = "/montage",
}) {
  const [editingTransition, setEditingTransition] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [inspectorDirty, setInspectorDirty] = useState(false);
  const editorRef = useRef(null);
  const segmentCardRef = useRef(null);

  // Find the active segment index
  const activeSegmentIndex = segments.findIndex(
    (s) => s.segment_id === activeSegmentId
  );
  const activeSegment = activeSegmentIndex >= 0 ? segments[activeSegmentIndex] : null;

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip when typing in inputs/textareas
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target.isContentEditable) return;

      const SEEK_STEP = 1000; // 1 second

      switch (e.key) {
        case " ": // Space → play / pause
          e.preventDefault();
          if (onPlayPause) onPlayPause();
          break;

        case "ArrowLeft": // ← → seek -1s
          e.preventDefault();
          if (onSeek) onSeek(Math.max(0, currentTimeMs - SEEK_STEP));
          break;

        case "ArrowRight": { // → → seek +1s
          e.preventDefault();
          const total = segments.reduce((s, seg) => s + (seg.duration || 0) * 1000, 0);
          if (onSeek) onSeek(Math.min(total, currentTimeMs + SEEK_STEP));
          break;
        }

        case "ArrowUp": // ↑ → previous segment
        case "k":
        case "K":
          e.preventDefault();
          if (segments.length > 0 && onSegmentClick) {
            const prev = activeSegmentIndex > 0 ? activeSegmentIndex - 1 : segments.length - 1;
            onSegmentClick(segments[prev], prev);
            setInspectorOpen(true);
          }
          break;

        case "ArrowDown": // ↓ → next segment
        case "j":
        case "J":
          e.preventDefault();
          if (segments.length > 0 && onSegmentClick) {
            const next = activeSegmentIndex < segments.length - 1 ? activeSegmentIndex + 1 : 0;
            onSegmentClick(segments[next], next);
            setInspectorOpen(true);
          }
          break;

        case "Delete":
        case "Backspace":
          if (activeSegment && onDeleteSegment) {
            e.preventDefault();
            onDeleteSegment(activeSegmentIndex);
          }
          break;

        case "Escape":
          if (editingTransition) {
            setEditingTransition(null);
          } else if (inspectorOpen) {
            setInspectorOpen(false);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    currentTimeMs, segments, activeSegmentIndex, activeSegment,
    inspectorOpen, editingTransition,
    onPlayPause, onSeek, onSegmentClick, onDeleteSegment,
  ]);

  // Timeline handlers
  const handleSegmentClick = useCallback(
    (segment, index) => {
      if (onSegmentClick) onSegmentClick(segment, index);
      setInspectorOpen(true);
    },
    [onSegmentClick]
  );

  const handleTransitionClick = useCallback((fromIndex, toIndex) => {
    setEditingTransition({ fromIndex, toIndex });
  }, []);

  const handleSaveTransition = useCallback(
    (data) => {
      if (onSaveTransition) onSaveTransition(data);
      setEditingTransition(null);
    },
    [onSaveTransition]
  );

  return (
    <div className="montage-editor" ref={editorRef} tabIndex={-1}>
      {/* Center column: Preview + Toolbar + Timeline */}
      <div className="montage-editor-center">
        {/* Preview */}
        <div className="montage-editor-preview">
          <VideoPreview
            segments={segments}
            currentTimeMs={currentTimeMs}
            isPlaying={isPlaying}
            onTimeUpdate={onTimeUpdate}
            onPlayPause={onPlayPause}
            onSeek={onSeek}
            aspectRatio={montage.aspect_ratio}
            previewUrl={previewUrl}
            previewStatus={previewStatus}
            previewLoading={previewLoading}
          />
        </div>

        {/* Toolbar */}
        <MontageToolbar
          segments={segments}
          saving={saving}
          loading={loading}
          enhancingPrompts={enhancingPrompts}
          generatingImages={generatingImages}
          generatingVideos={generatingVideos}
          imageQuality={imageQuality}
          videoQuality={videoQuality}
          onSave={onSave}
          onEnhanceAll={onEnhanceAll}
          onGenerateImages={onGenerateImages}
          onGenerateVideos={onGenerateVideos}
          onAddSegment={onAddSegment}
          onSetImageQuality={onSetImageQuality}
          onSetVideoQuality={onSetVideoQuality}
          harmonizing={harmonizing}
          onHarmonize={onHarmonize}
          exporting={exporting}
          onExport={onExport}
        />

        {/* Timeline (persistent) */}
        <div className="montage-editor-timeline">
          <VideoTimeline
            segments={segments}
            currentTimeMs={currentTimeMs}
            onSeek={onSeek}
            onSegmentReorder={onSegmentReorder}
            onSegmentClick={handleSegmentClick}
            onTransitionClick={handleTransitionClick}
            onAddSegment={onAddSegment}
            activeSegmentId={activeSegmentId}
          />
        </div>
      </div>

      {/* Right rail: Segment Inspector */}
      {inspectorOpen && activeSegment && (
        <aside className="montage-editor-inspector">
          <div className="inspector-header">
            <h3 className="inspector-title">
              <span className="inspector-badge">{activeSegmentIndex + 1}</span>
              {t("montage.editor.inspector.title")}
            </h3>
            <div className="inspector-header-right">
              <span className="inspector-duration">
                <span className="material-symbols-rounded" style={{ fontSize: 16, opacity: 0.7 }}>schedule</span>
                {activeSegment.duration}s
              </span>
              {inspectorDirty && (
                <>
                  <button
                    type="button"
                    className="inspector-action-btn inspector-save-btn"
                    onClick={() => segmentCardRef.current?.save()}
                    disabled={saving}
                    title={t("montage.editor.inspector.save")}
                  >
                    <span className="material-symbols-rounded">
                      {saving ? "progress_activity" : "check"}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="inspector-action-btn inspector-cancel-btn"
                    onClick={() => segmentCardRef.current?.cancel()}
                    disabled={saving}
                    title={t("montage.editor.inspector.cancel")}
                  >
                    <span className="material-symbols-rounded">undo</span>
                  </button>
                </>
              )}
              <button
                type="button"
                className="inspector-close"
                onClick={() => setInspectorOpen(false)}
                title={t("montage.editor.inspector.close")}
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          </div>

          <div className="inspector-body">
            <div className="montage-reference-panel">
              <MediaSelector
                title={t("montage.editor.inspector.referenceMedia")}
                selected={referenceMedia}
                onChange={onUpdateReferences}
                maxCount={8}
                allowUpload
                allowLibrary
                pickerTarget={referencePickerTarget}
                pickerReturn={referencePickerReturn}
                pickerMulti
                gridLimit={6}
              />
            </div>
            <SegmentCard
              ref={segmentCardRef}
              segment={activeSegment}
              index={activeSegmentIndex}
              prevSegment={activeSegmentIndex > 0 ? segments[activeSegmentIndex - 1] : null}
              montageId={montage.montage_id || ""}
              aspectRatio={montage.aspect_ratio || ""}
              showEndImage={videoQuality === "high"}
              onUpdate={(updatedSegment) =>
                onUpdateSegment(activeSegmentIndex, updatedSegment)
              }
              onSaveSegment={(updatedSegment) =>
                onSaveSegment?.(activeSegmentIndex, updatedSegment)
              }
              onDelete={() => onDeleteSegment(activeSegmentIndex)}
              onRegenerate={() => onRegenerateSegment(activeSegmentIndex)}
              onEnhancePrompt={() => onEnhanceSegmentPrompt(activeSegmentIndex)}
              onGenerateStartImage={() => onGenerateImages?.([activeSegment.segment_id], ["start"])}
              onGenerateEndImage={() => onGenerateImages?.([activeSegment.segment_id], ["end"])}
              onGenerateVideo={() => onGenerateVideos?.([activeSegment.segment_id])}
              onAddReference={onAddReferenceFromSegment}
              onDirtyChange={setInspectorDirty}
              forceEdit
              saving={saving}
              readonly={false}
            />
          </div>
        </aside>
      )}

      {/* Inspector closed toggle */}
      {!inspectorOpen && activeSegment && (
        <button
          type="button"
          className="inspector-toggle-open"
          onClick={() => setInspectorOpen(true)}
          title="Ouvrir l'inspecteur"
        >
          <span className="material-symbols-rounded">chevron_left</span>
        </button>
      )}

      {/* No segment selected hint */}
      {!activeSegment && segments.length > 0 && (
        <aside className="montage-editor-inspector montage-editor-inspector-empty">
          <div className="inspector-empty-content">
            <div className="montage-reference-panel">
              <MediaSelector
                title="Médias de référence"
                selected={referenceMedia}
                onChange={onUpdateReferences}
                maxCount={8}
                allowUpload
                allowLibrary
                pickerTarget={referencePickerTarget}
                pickerReturn={referencePickerReturn}
                pickerMulti
                gridLimit={6}
              />
            </div>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 32, opacity: 0.3 }}
            >
              touch_app
            </span>
            <p>{t("montage.editor.inspector.empty")}</p>
          </div>
        </aside>
      )}

      {/* Transition Editor Modal */}
      {editingTransition && (
        <div className="montage-editor-transition-overlay">
          <TransitionEditor
            fromSegment={segments[editingTransition.fromIndex]}
            toSegment={segments[editingTransition.toIndex]}
            onSave={handleSaveTransition}
            onClose={() => setEditingTransition(null)}
          />
        </div>
      )}
    </div>
  );
}
